// Layer 4 (UI): "what is off" -- Q5 (paul-call.A7 #255). polish.P11: the
// plant model's What is off tab; series drawn in ink (crimson never colours
// a data series), captions wrap rather than clip. One `ControlChart`
// per non-quiet variable in an open, non-green circuit, worst first, plus
// the upstream mill-feed chart over the same window. A green circuit never
// mounts this component (`Scene.tsx` only renders it when `pane` exists).
import { SurfacePagePanel } from '@/design-system';
import { cn } from '@/shared/cn';
import { ControlChart } from '@/versions/v1/modules/_viz';
import type { OffChart, OffPane as OffPaneModel } from './Circuits.transform';
import { captionCase } from './OffPane.transform';

const WINDOW_LABEL: Record<OffPaneModel['window'], string> = {
  '24h': 'last 24 h',
  '7d': 'last 7 d',
};

// CI-F8: the caption carries the qualifiers (tag · since · case); the chart
// itself renders `hideLabel` so the variable's label prints exactly once,
// not once in the caption and again in the chart's own title.
// CI-F21 (NOTE, fixed as cheap): a windowed series under 4 points reads as
// a chart that failed to load rather than as evidence -- the caption names
// the point count so the reader knows it's honest, not broken.
const SPARSE_POINT_THRESHOLD = 4;

// CI-F26 (fix round 2): the CI-F8 fix stopped the label printing twice, but
// left the tag printing twice on the upstream card -- the seed's own label
// already ends in its parenthetical tag ("Ball mill feed rate (WIC1201A)"),
// so appending "· WIC1201A" repeated it. Omit the tag segment whenever the
// label already carries it (either card can hit this, not just upstream).
function captionTag(label: string, tag: string | undefined): string {
  if (!tag || label.includes(tag)) return '';
  return ` · ${tag}`;
}

/** The comp's chart height: tall enough that a beyond-limit run reads as a run. */
const CHART_HEIGHT = 180;

function ChartCard({ item }: { item: OffChart }) {
  const sparse = item.chart.points.length < SPARSE_POINT_THRESHOLD;
  const caseChip = captionCase(item);
  return (
    <div className="min-w-0">
      <p className="mb-1 flex flex-wrap items-center gap-x-1.5 gap-y-1 break-words text-xs font-medium text-zinc-900 dark:text-zinc-100">
        <span>
          {item.label}
          {captionTag(item.label, item.tag)}
          {item.since ? ` · since ${item.since}` : ''}
          {sparse ? ` · ${item.chart.points.length} pts` : ''}
        </span>
        {caseChip ? (
          <span
            data-off-case={caseChip}
            className="inline-flex items-center whitespace-nowrap rounded-full bg-[var(--hairline)] px-2 py-px text-[10px] font-semibold uppercase leading-4 tracking-[0.05em] text-ink-secondary"
          >
            {caseChip}
          </span>
        ) : null}
      </p>
      <ControlChart model={item.chart} height={CHART_HEIGHT} hideLabel seriesTone="ink" pointMarkers="highlighted" />
    </div>
  );
}

export function OffPane({ circuitLabel, pane }: { circuitLabel: string; pane: OffPaneModel }) {
  return (
    <SurfacePagePanel title={`What is off — ${circuitLabel} · ${WINDOW_LABEL[pane.window]}`}>
      {/*
        CI-F5: `ScrollCue` (design-system, frozen) counts hidden rows via
        `tbody tr` / `li` / `[data-scroll-cue-row]`, one per matched element —
        it has no notion of a CSS-grid *row*, so a 2-up grid with a
        `maxHeight` cap it can't actually fit undercounts (or, as shipped,
        silently miscounts to zero while a card visibly clips). Q5's own cap
        is "≤ 2 charts tall" (this pane never carries more than three cards:
        the non-quiet parameters plus the one upstream chart), so instead of
        fighting the primitive's row math, the pane takes its natural height
        — no cap, no clipping by construction.
      */}
      <div className={cn('grid grid-cols-1 gap-4 sm:grid-cols-2', pane.charts.length >= 2 ? 'xl:grid-cols-3' : '')}>
        {pane.charts.map((item) => (
          <ChartCard key={item.variableId} item={item} />
        ))}
        <div className="min-w-0">
          <p className="mb-1 break-words text-xs font-medium text-zinc-900 dark:text-zinc-100">
            {pane.upstream.label}
            {captionTag(pane.upstream.label, pane.upstream.tag)}
            {pane.upstream.windowNote ? ` · ${pane.upstream.windowNote}` : ''}
          </p>
          <ControlChart model={pane.upstream.chart} height={CHART_HEIGHT} hideLabel seriesTone="ink" pointMarkers="highlighted" />
        </div>
      </div>
    </SurfacePagePanel>
  );
}
